import { DataTable } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { IEEE_754_CONVERTER_STRINGS } from "@/components/tools/ieee-754-converter";
import { t } from "@/lib/i18n/interpolate";
import { superscript } from "@/lib/tool-kit/cs-tools/bits";
import {
  type Analysis,
  analysePattern,
  type FloatFormat,
  formatFacts,
  type plainDecimal,
  type Scientific,
  shortText,
} from "@/lib/tool-kit/cs-tools/ieee754";

export type Strings = typeof IEEE_754_CONVERTER_STRINGS;
export type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const PLAIN: Intl.NumberFormatOptions = {
  useGrouping: false,
  maximumFractionDigits: 0,
};
const LOG10_2 = Math.log10(2);

function groupFields(a: Analysis): {
  sign: string;
  exponent: string;
  fraction: string;
} {
  const f = a.format;
  return {
    sign: a.bits.slice(0, 1),
    exponent: a.bits.slice(1, 1 + f.exponentBits),
    fraction: a.bits.slice(1 + f.exponentBits),
  };
}

/** The three colour-coded fields, drawn as one strip. Decorative: the table below says the same. */
export function BitStrip({ a }: { a: Analysis }) {
  const g = groupFields(a);
  return (
    <p aria-hidden="true" className="flex flex-wrap gap-2 font-mono text-sm">
      <span className="rounded border border-red-500/50 bg-red-500/10 px-2 py-1">
        {g.sign}
      </span>
      <span className="rounded border border-amber-500/50 bg-amber-500/10 px-2 py-1">
        {g.exponent}
      </span>
      <span className="break-all rounded border border-sky-500/50 bg-sky-500/10 px-2 py-1">
        {g.fraction}
      </span>
    </p>
  );
}

function exponentMeaning(a: Analysis, strings: Strings, fmt: Fmt): string {
  const f = a.format;
  const raw = fmt(Number(a.fields.exponent), PLAIN);
  if (a.kind === "normal" && a.exponent !== null) {
    return t(strings.exponentNormal, {
      raw,
      bias: fmt(f.bias, PLAIN),
      unbiased: fmt(a.exponent, PLAIN),
    });
  }
  if (a.kind === "subnormal") {
    return t(strings.exponentSubnormal, { min: fmt(f.minExponent, PLAIN) });
  }
  if (a.kind === "zero") {
    return strings.exponentZero;
  }
  return strings.exponentAllOnes;
}

function fractionMeaning(a: Analysis, strings: Strings): string {
  if (a.nan) {
    return t(strings.fractionNan, {
      payload: a.nan.payload.toString(),
      kind: a.nan.quiet ? strings.kindQuietNan : strings.kindSignallingNan,
    });
  }
  if (a.significand) {
    return t(strings.fractionSignificand, { significand: a.significand });
  }
  return strings.fractionNone;
}

export function FieldsTable({
  a,
  strings,
  fmt,
}: {
  a: Analysis;
  strings: Strings;
  fmt: Fmt;
}) {
  const g = groupFields(a);
  return (
    <DataTable
      caption={strings.fieldsCaption}
      head={[strings.colField, strings.colBits, strings.colMeaning]}
      rows={[
        [
          strings.rowSign,
          g.sign,
          a.fields.sign === 1 ? strings.signNegative : strings.signPositive,
        ],
        [strings.rowExponent, g.exponent, exponentMeaning(a, strings, fmt)],
        [strings.rowFraction, g.fraction, fractionMeaning(a, strings)],
      ]}
    />
  );
}

export function kindLabel(a: Analysis, strings: Strings): string {
  switch (a.kind) {
    case "zero":
      return strings.kindZero;
    case "subnormal":
      return strings.kindSubnormal;
    case "normal":
      return strings.kindNormal;
    case "infinity":
      return strings.kindInfinity;
    default:
      return a.nan?.quiet ? strings.kindQuietNan : strings.kindSignallingNan;
  }
}

export function FormulaLine({ a, strings }: { a: Analysis; strings: Strings }) {
  if (a.exponent === null || a.significand === null) {
    return null;
  }
  const exponentText =
    a.exponent < 0 ? `⁻${superscript(-a.exponent)}` : superscript(a.exponent);
  return (
    <p className="break-all font-mono text-foreground text-sm">
      {t(strings.formulaLine, {
        sign: superscript(a.fields.sign),
        exponent: exponentText,
        significand: a.significand,
      })}
    </p>
  );
}

function RoundingNotes({ a, strings }: { a: Analysis; strings: Strings }) {
  const notes: string[] = [];
  if (a.overflow) {
    notes.push(strings.overflowNote);
  }
  if (a.underflow) {
    notes.push(strings.underflowNote);
  }
  if (a.kind === "subnormal") {
    notes.push(strings.subnormalNote);
  }
  if (notes.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1">
      {notes.map((note) => (
        <p className="text-muted-foreground text-sm" key={note}>
          {note}
        </p>
      ))}
    </div>
  );
}

function sciText(value: Scientific | null): string {
  if (!value) {
    return "—";
  }
  const sign = value.negative ? "−" : "+";
  const exponentSign = value.exponent < 0 ? "−" : "+";
  return `${sign}${value.digits[0]}.${value.digits.slice(1)}e${exponentSign}${Math.abs(value.exponent)}`;
}

export function ErrorSection({
  a,
  enteredText,
  strings,
}: {
  a: Analysis;
  enteredText: string;
  strings: Strings;
}) {
  const err = a.error;
  if (!err) {
    return null;
  }
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.errorHeading}</h3>
      {err.exact ? (
        <p className="text-muted-foreground text-sm">
          {t(strings.errorNone, { format: a.format.name })}
        </p>
      ) : (
        <DataTable
          caption={strings.errorCaption}
          head={[strings.colQuantity, strings.colValue]}
          rows={[
            [strings.errorEntered, enteredText],
            [
              t(strings.errorStored, { format: a.format.name }),
              a.shortest ?? "",
            ],
            [strings.errorAbsolute, sciText(err.absolute)],
            [strings.errorRelative, sciText(err.relative)],
            [strings.errorUlp, err.ulpFraction ?? "—"],
          ]}
        />
      )}
      <RoundingNotes a={a} strings={strings} />
      {err.exact ? null : (
        <p className="text-muted-foreground text-xs">{strings.ulpNote}</p>
      )}
    </section>
  );
}

function shortOf(
  pattern: bigint | null,
  f: FloatFormat,
  strings: Strings
): [string, string] {
  if (pattern === null) {
    return [strings.none, strings.none];
  }
  const analysis = analysePattern(pattern, f);
  return [analysis.hex, analysis.shortest ?? kindOnly(analysis, strings)];
}

function kindOnly(a: Analysis, strings: Strings): string {
  const sign = a.fields.sign === 1 ? "−" : "+";
  return `${sign}${strings.infinitySymbol}`;
}

export function NeighboursTable({
  a,
  strings,
}: {
  a: Analysis;
  strings: Strings;
}) {
  if (a.kind === "nan") {
    return null;
  }
  const f = a.format;
  const [downHex, downValue] = shortOf(a.down, f, strings);
  const [upHex, upValue] = shortOf(a.up, f, strings);
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.neighboursHeading}</h3>
      <DataTable
        caption={strings.neighboursCaption}
        head={[strings.colPosition, strings.colHex, strings.colValue]}
        rows={[
          [strings.nbDown, downHex, downValue],
          [strings.nbThis, a.hex, a.shortest ?? kindOnly(a, strings)],
          [strings.nbUp, upHex, upValue],
        ]}
      />
    </section>
  );
}

export function FactsTable({
  f,
  strings,
  fmt,
}: {
  f: FloatFormat;
  strings: Strings;
  fmt: Fmt;
}) {
  const facts = formatFacts(f);
  const precision = f.fractionBits + 1;
  const show = (d: Parameters<typeof plainDecimal>[0]) => shortText(d);
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>
        {t(strings.factsHeading, { format: f.name })}
      </h3>
      <DataTable
        caption={strings.factsCaption}
        head={[strings.colQuantity, strings.colValue]}
        rows={[
          [
            strings.factLayout,
            t(strings.factLayoutValue, {
              exponent: fmt(f.exponentBits, PLAIN),
              fraction: fmt(f.fractionBits, PLAIN),
              total: fmt(f.total, PLAIN),
            }),
          ],
          [strings.factBias, fmt(f.bias, PLAIN)],
          [
            strings.factRange,
            t(strings.factRangeValue, {
              min: fmt(f.minExponent, PLAIN),
              max: fmt(f.maxExponent, PLAIN),
            }),
          ],
          [strings.factSubnormal, show(facts.smallestSubnormal)],
          [strings.factNormal, show(facts.smallestNormal)],
          [strings.factLargest, show(facts.largestFinite)],
          [strings.factEpsilon, show(facts.epsilon)],
          [
            strings.factDigits,
            fmt(Math.floor((precision - 1) * LOG10_2), PLAIN),
          ],
          [
            strings.factRoundTrip,
            fmt(Math.ceil(1 + precision * LOG10_2), PLAIN),
          ],
        ]}
      />
    </section>
  );
}
